import {
  AllocationRowData,
  WorkloadTableRowData,
} from "@src/types/role_request_types";
import dayjs from "dayjs";
import _ from "lodash";

/**
 * Calculates the start and end date that will be used for the allocation column definitions
 *
 * @param {WorkloadTableRowData[]} rowData row data of the table
 * @returns startDate and endDate that needs to be considered for the column definitions
 */
export function getWorkloadDates(rowData: WorkloadTableRowData[]): {
  startDate: Date | null;
  endDate: Date | null;
} {
  if (!rowData?.length) return { startDate: null, endDate: null };

  const { startDate: workloadStartDate, endDate: workloadEndDate } =
    getStartAndEndDates(rowData[0].allocations);

  let startDate: Date = workloadStartDate;

  // ensure end date is at least 10 months after the start date
  const minEndDate: Date = dayjs(workloadStartDate)
    .add(9, "months")
    .endOf("month")
    .toDate();

  let endDate: Date = dayjs(workloadEndDate).isBefore(minEndDate)
    ? minEndDate
    : workloadEndDate;

  // if proposed allocation exist, consider them for the start and end date calculation because they might differ from the original request
  const hasProposedAllocations: boolean = rowData.length > 1;

  if (hasProposedAllocations) {
    const { startDate: proposedStartDate, endDate: proposedEndDate } =
      getStartAndEndDates(rowData[1].allocations);

    if (dayjs(proposedStartDate).isBefore(workloadStartDate, "month")) {
      startDate = proposedStartDate;
    }

    if (dayjs(proposedEndDate).isAfter(workloadEndDate, "month")) {
      endDate = proposedEndDate;
    }
  }

  return { startDate, endDate };
}

/**
 * Calculates the first and last date of a given Map by sorting the keys and take the first and last one.
 *
 * @param {Map<string, AllocationRowData>} allocationMap the Map to be considered for the calculation
 * @returns startDate and EndDate of the Maps keys
 */
function getStartAndEndDates(allocationMap: Map<string, AllocationRowData>): {
  startDate: Date;
  endDate: Date;
} {
  const allocationKeys: string[] = [...allocationMap.keys()];
  const sortedKeys: string[] = _.orderBy(allocationKeys, (key: string) =>
    dayjs(key, "M-YYYY")
  );

  const firstKey: string = sortedKeys[0];
  const lastKey: string = _.last(sortedKeys);

  const startDate: Date = dayjs(firstKey, "M-YYYY").toDate();
  const endDate: Date = dayjs(lastKey, "M-YYYY").toDate();

  return { startDate, endDate };
}
